import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { User, Briefcase, Building2, FolderKanban, ExternalLink, Loader2, Receipt, CheckCheck, Check, Users, Pause, Play, CheckCircle2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;

const ENDPOINT = {
  worker: (id) => `worker-drilldown?user_id=${id}`,
  job: (id) => `job-drilldown?job_id=${id}`,
  customer: (id) => `customer-drilldown?customer_id=${id}`,
  project: (id) => `project-drilldown?project_id=${id}`,
};
const ICON = { worker: User, job: Briefcase, customer: Building2, project: FolderKanban };
const fmtH = (v) => (v == null ? '—' : `${Number(v).toFixed(2)} h`);
const fmtM = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtD = (v) => (v ? new Date(v).toLocaleDateString() : '—');

const Stat = ({ label, value, accent, testId }) => (
  <div className="rounded-lg p-2.5" style={{ background: '#1A2433', border: '1px solid #2D3748' }}>
    <p className="text-[10px] uppercase tracking-wide" style={{ color: '#64748B' }}>{label}</p>
    <p className="text-base font-semibold" style={{ color: accent || '#fff' }} data-testid={testId}>{value}</p>
  </div>
);

const Section = ({ title, children, testId }) => (
  <div data-testid={testId}>
    <p className="text-[11px] font-bold uppercase tracking-widest mb-1.5" style={{ color: '#64748B' }}>{title}</p>
    {children}
  </div>
);

const Bars = ({ rows, nameKey, valueKey = 'hours', onPick, testId }) => {
  const max = Math.max(1, ...rows.map((r) => Number(r[valueKey]) || 0));
  return (
    <div className="space-y-1" data-testid={testId}>
      {rows.length === 0 && <p className="text-xs" style={{ color: '#64748B' }}>Nothing in this period.</p>}
      {rows.map((r, i) => (
        <button key={i} type="button" onClick={onPick ? () => onPick(r) : undefined} disabled={!onPick}
          className={`w-full text-left rounded px-2 py-1 ${onPick ? 'hover:bg-white/5' : ''}`}>
          <div className="flex justify-between text-xs mb-0.5">
            <span className={onPick ? 'text-sky-300 underline-offset-2 hover:underline' : 'text-white'}>{r[nameKey] || '—'}</span>
            <span style={{ color: '#A0AEC0' }}>{fmtH(r[valueKey])}</span>
          </div>
          <div className="h-1.5 rounded-full" style={{ background: '#2D3748' }}>
            <div className="h-1.5 rounded-full" style={{ width: `${(Number(r[valueKey]) / max) * 100}%`, background: '#1E88E5' }} />
          </div>
        </button>
      ))}
    </div>
  );
};

const JobList = ({ jobs, onJob }) => (
  <div className="space-y-1">
    {jobs.length === 0 && <p className="text-xs" style={{ color: '#64748B' }}>None.</p>}
    {jobs.map((j) => (
      <button key={j.id} type="button" onClick={() => onJob(j)} className="w-full text-left rounded px-2 py-1.5 hover:bg-white/5 flex items-center justify-between gap-2"
        data-testid={`drill-job-${j.id}`}>
        <span className="min-w-0">
          <span className="text-xs font-mono mr-1.5" style={{ color: '#10B981' }}>{j.job_number}</span>
          <span className="text-sm text-sky-300 truncate">{j.title}</span>
          {j.customer_name && <span className="text-[11px] ml-1.5" style={{ color: '#64748B' }}>· {j.customer_name}</span>}
        </span>
        <span className="text-[11px] whitespace-nowrap" style={{ color: '#A0AEC0' }}>{j.status}{j.hours != null ? ` · ${fmtH(j.hours)}` : ''}</span>
      </button>
    ))}
  </div>
);

// Right-side drill-down for Worker / Job / Customer / Project rows in any report.
// `target` = { type, id, label }; range = { start, end }.
export default function DrillDrawer({ target, range, token, onClose, onNavigate }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [stack, setStack] = useState([]);
  const [busy, setBusy] = useState(null);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [staff, setStaff] = useState([]);
  const [crewIds, setCrewIds] = useState([]);
  const [reload, setReload] = useState(0);
  const navigate = useNavigate();
  const { toast } = useToast();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const cur = stack.length ? stack[stack.length - 1] : target;
  const errText = (e) => { const d = e?.response?.data?.detail; return typeof d === 'string' ? d : d?.message || 'Request failed'; };

  // ── Quick actions ──
  const approveEntries = async (ids) => {
    setBusy('approve');
    let ok = 0;
    for (const id of ids) {
      try { await axios.put(`${API}/time/${id}/supervisor-approve`, {}, auth); ok += 1; } catch (e) { toast({ title: 'Could not approve a shift', description: errText(e), variant: 'destructive' }); }
    }
    setBusy(null);
    if (ok) { toast({ title: `Approved ${ok} shift${ok === 1 ? '' : 's'}` }); setReload((n) => n + 1); }
  };
  const jobAction = async (action, jobId) => {
    setBusy(action);
    try {
      if (action === 'pause') await axios.put(`${API}/jobs/${jobId}/pause`, { reason: 'Paused by admin from report drill-down' }, auth);
      else if (action === 'resume') await axios.put(`${API}/jobs/${jobId}/resume`, {}, auth);
      else if (action === 'complete') await axios.put(`${API}/jobs/${jobId}/complete?override_photos=true`, {}, auth);
      toast({ title: `Job ${action === 'complete' ? 'completed' : action + 'd'}` });
      setReload((n) => n + 1);
    } catch (e) { toast({ title: `Could not ${action} job`, description: errText(e), variant: 'destructive' }); }
    finally { setBusy(null); }
  };
  const openReassign = async (crew) => {
    setCrewIds(crew.map((c) => c.id));
    if (!staff.length) {
      try { const { data: u } = await axios.get(`${API}/users`, auth); setStaff((Array.isArray(u) ? u : []).filter((x) => !x.deleted_at && x.is_active !== false)); } catch { /* keep empty */ }
    }
    setReassignOpen(true);
  };
  const saveReassign = async (jobId) => {
    if (!crewIds.length) { toast({ title: 'Pick at least one worker', variant: 'destructive' }); return; }
    setBusy('reassign');
    try {
      await axios.put(`${API}/jobs/${jobId}`, { assigned_to: crewIds[0], assigned_user_ids: crewIds }, auth);
      toast({ title: 'Crew updated' }); setReassignOpen(false); setReload((n) => n + 1);
    } catch (e) { toast({ title: 'Could not reassign', description: errText(e), variant: 'destructive' }); }
    finally { setBusy(null); }
  };

  useEffect(() => { setStack([]); }, [target?.type, target?.id]);

  useEffect(() => {
    if (!cur?.id) return undefined;
    let alive = true;
    setData(null); setErr(null);
    const q = ENDPOINT[cur.type](cur.id);
    const rp = range?.start ? `&start_date=${range.start}&end_date=${range.end}` : '';
    axios.get(`${API}/reports/v2/${q}${rp}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data: d }) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(e.response?.data?.detail || 'Could not load details'); });
    return () => { alive = false; };
  }, [cur?.type, cur?.id, range?.start, range?.end, token, reload]);

  const push = (type, id, label) => setStack((s) => [...s, { type, id, label }]);
  const go = (path) => { onClose(); navigate(path); };
  const Icon = ICON[cur?.type] || User;

  return (
    <Sheet open={!!target} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto border-l border-[#2D3748] text-white" style={{ background: '#0F1923' }} data-testid="drill-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-white">
            <Icon className="w-5 h-5" style={{ color: '#1E88E5' }} />
            <span data-testid="drill-title">{data?.worker?.name || data?.job?.title || data?.customer?.name || data?.project?.name || cur?.label || 'Details'}</span>
          </SheetTitle>
          <SheetDescription style={{ color: '#64748B' }}>
            {cur?.type === 'worker' && 'Worker drill-down'}{cur?.type === 'job' && 'Job drill-down'}{cur?.type === 'customer' && 'Customer drill-down'}{cur?.type === 'project' && 'Project drill-down'}
            {data?.period ? ` · ${data.period.start} → ${data.period.end}` : ''}
          </SheetDescription>
          {stack.length > 0 && (
            <button type="button" onClick={() => setStack((s) => s.slice(0, -1))} className="text-xs text-left text-sky-300 hover:underline" data-testid="drill-back">← Back</button>
          )}
        </SheetHeader>

        {err && <p className="mt-4 text-sm text-red-400" data-testid="drill-error">{err}</p>}
        {!data && !err && <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: '#1E88E5' }} /></div>}

        {data && cur.type === 'worker' && (
          <div className="mt-4 space-y-5">
            <p className="text-xs" style={{ color: '#A0AEC0' }}>{data.worker.email}{data.worker.team ? ` · ${data.worker.team}` : ''}{data.worker.job_title ? ` · ${data.worker.job_title}` : ''}</p>
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Days worked" value={data.attendance.days_worked} testId="drill-days" />
              <Stat label="Shifts" value={data.attendance.shifts} />
              <Stat label="Absences" value={data.attendance.absences} accent={data.attendance.absences ? '#F87171' : undefined} />
              <Stat label="Total hours" value={fmtH(data.hours.total)} testId="drill-hours" />
              <Stat label="Billable (on jobs)" value={fmtH(data.hours.billable)} accent="#10B981" />
              <Stat label="Non-billable" value={fmtH(data.hours.non_billable)} accent="#FFA000" />
              <Stat label="OT est. (>8h/day)" value={fmtH(data.hours.overtime_est)} />
              <Stat label="General Work" value={fmtH(data.hours.general_work)} />
              <Stat label="Labour cost" value={fmtM(data.hours.labor_cost)} />
            </div>
            <Section title="Hours by project"><Bars rows={data.by_project} nameKey="name" /></Section>
            <Section title="Top jobs by hours" testId="drill-top-jobs"><JobList jobs={data.top_jobs} onJob={(j) => push('job', j.id, j.title)} /></Section>
            <Section title={`Completed jobs (${data.completed_jobs.length})`}><JobList jobs={data.completed_jobs} onJob={(j) => push('job', j.id, j.title)} /></Section>
            <Section title={`Pending approval (${data.pending_entries?.length || 0})`} testId="drill-pending">
              {(data.pending_entries || []).length === 0 ? <p className="text-xs" style={{ color: '#64748B' }}>No submitted shifts waiting in this period.</p> : (
                <div className="space-y-1 text-xs">
                  <div className="flex justify-end">
                    <button type="button" disabled={busy === 'approve'} onClick={() => approveEntries(data.pending_entries.map((e) => e.id))}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg font-semibold disabled:opacity-60" style={{ background: '#00C896', color: '#0B1321' }} data-testid="drill-approve-all">
                      {busy === 'approve' ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCheck className="w-3 h-3" />} Approve all
                    </button>
                  </div>
                  {data.pending_entries.map((e) => (
                    <div key={e.id} className="flex items-center justify-between px-2 py-1 rounded gap-2" style={{ background: '#1A2433' }} data-testid={`drill-pending-${e.id}`}>
                      <span className="text-white">{fmtD(e.clock_in)} <span style={{ color: '#64748B' }}>{e.project || ''}{e.job_title ? ` · ${e.job_title}` : ''}</span></span>
                      <span className="flex items-center gap-2"><span style={{ color: '#A0AEC0' }}>{fmtH(e.hours)}</span>
                        <button type="button" disabled={busy === 'approve'} onClick={() => approveEntries([e.id])} className="p-1 rounded hover:bg-emerald-500/20" title="Approve" data-testid={`drill-approve-${e.id}`}>
                          <Check className="w-3.5 h-3.5" style={{ color: '#00C896' }} /></button></span>
                    </div>
                  ))}
                </div>
              )}
            </Section>
            <Section title="Recent shifts">
              <div className="space-y-1 text-xs">
                {data.recent_entries.map((e) => (
                  <div key={e.id} className="flex justify-between px-2 py-1 rounded" style={{ background: '#1A2433' }}>
                    <span className="text-white">{fmtD(e.clock_in)} <span style={{ color: '#64748B' }}>{e.project || ''}{e.job_title ? ` · ${e.job_title}` : ''}</span></span>
                    <span style={{ color: '#A0AEC0' }}>{fmtH(e.hours)}{!e.clock_out ? ' · open' : ''}</span>
                  </div>
                ))}
              </div>
            </Section>
            <Related onNavigate={onNavigate} items={[
              { key: 'payroll', label: 'Payroll (this worker)', filters: { worker: data.worker.id, q: data.worker.name, _from: `${data.worker.name} (worker)` } },
              { key: 'billable-hours', label: 'Billable hours', filters: { worker: data.worker.id, _from: `${data.worker.name} (worker)` } },
              { key: 'worker-trend', label: 'Worker trend', filters: { worker: data.worker.id, _from: `${data.worker.name} (worker)` } },
              { key: 'attendance', label: 'Attendance', filters: { _from: `${data.worker.name} (worker)` } },
            ]} />
            <div className="flex flex-wrap gap-2 pt-1">
              <Link onClick={() => go(`/staff?user=${data.worker.id}`)} testId="drill-open-staff"><ExternalLink className="w-3 h-3 inline mr-1" />Staff profile</Link>
            </div>
          </div>
        )}

        {data && cur.type === 'job' && (
          <div className="mt-4 space-y-5">
            <p className="text-xs" style={{ color: '#A0AEC0' }}>
              <span className="font-mono" style={{ color: '#10B981' }}>{data.job.job_number}</span> · {data.job.status}{data.job.priority ? ` · ${data.job.priority}` : ''}
              {data.job.address ? <><br /><span data-testid="drill-job-address">{data.job.address}</span></> : <><br /><span style={{ color: '#64748B' }}>No address on job</span></>}
            </p>
            <div className="grid grid-cols-2 gap-2 text-xs rounded-lg p-2.5" style={{ background: '#1A2433', border: '1px solid #2D3748' }} data-testid="drill-job-details">
              <div><span style={{ color: '#64748B' }}>Service / type</span><br /><span className="text-white">{data.job.labels?.length ? data.job.labels.join(', ') : '—'}</span></div>
              <div><span style={{ color: '#64748B' }}>Billing</span><br /><span className="text-white">{data.job.service_rate ? `${fmtM(data.job.service_rate)}${data.job.billing_mode === 'flat' ? ' flat fee' : ' / h'}` : 'No rate set'}</span></div>
              <div><span style={{ color: '#64748B' }}>Started · Due</span><br /><span className="text-white">{fmtD(data.job.started_at || data.job.first_session)} · {fmtD(data.job.due_date || data.job.scheduled_completion)}</span></div>
              <div><span style={{ color: '#64748B' }}>Completed · Duration</span><br /><span className="text-white">{fmtD(data.job.completed_at)} · {data.job.duration_days != null ? `${data.job.duration_days} day${data.job.duration_days === 1 ? '' : 's'}` : '—'}</span></div>
              <div><span style={{ color: '#64748B' }}>First / last session</span><br /><span className="text-white">{fmtD(data.job.first_session)} → {fmtD(data.job.last_session)}</span></div>
              <div><span style={{ color: '#64748B' }}>Project</span><br /><span className="text-white">{data.job.project_number ? `${data.job.project_number} — ${data.job.project_name}` : '—'}</span></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Job hours" value={fmtH(data.hours.job_sessions)} testId="drill-job-hours" />
              <Stat label="Payroll tagged" value={fmtH(data.hours.payroll_tagged)} />
              <Stat label="Variance" value={fmtH(data.hours.variance)} accent={Math.abs(data.hours.variance) > 1 ? '#FFA000' : undefined} />
              <Stat label="Billable est." value={fmtM(data.hours.billable_estimate)} accent="#10B981" />
              <Stat label="Sessions" value={data.sessions} />
              <Stat label="Photos / notes" value={`${data.job.photos} / ${data.job.notes}`} />
            </div>
            {data.job.description && <p className="text-sm" style={{ color: '#CBD5E1' }}>{data.job.description}</p>}
            <Section title="Customer & project">
              <div className="flex flex-wrap gap-2 text-sm">
                {data.job.customer_id ? <Link onClick={() => push('customer', data.job.customer_id, data.job.customer_name)} testId="drill-job-customer">{data.job.customer_name || 'Customer'}</Link> : <span style={{ color: '#64748B' }}>No customer</span>}
                {data.job.project_id ? <Link onClick={() => push('project', data.job.project_id, data.job.project_name)} testId="drill-job-project">{data.job.project_number} — {data.job.project_name}</Link> : <span style={{ color: '#64748B' }}>No project</span>}
              </div>
            </Section>
            <Section title="Quick actions" testId="drill-job-actions">
              <div className="flex flex-wrap gap-2">
                <Link onClick={() => openReassign(data.crew)} testId="drill-reassign-btn"><Users className="w-3 h-3 inline mr-1" />Reassign crew</Link>
                {data.job.status === 'in_progress' && <Link onClick={() => jobAction('pause', data.job.id)} testId="drill-pause-btn"><Pause className="w-3 h-3 inline mr-1" />Pause</Link>}
                {data.job.status === 'paused' && <Link onClick={() => jobAction('resume', data.job.id)} testId="drill-resume-btn"><Play className="w-3 h-3 inline mr-1" />Resume</Link>}
                {!['completed', 'invoiced', 'cancelled'].includes(data.job.status) && <Link onClick={() => { if (window.confirm('Mark this job complete?')) jobAction('complete', data.job.id); }} testId="drill-complete-btn"><CheckCircle2 className="w-3 h-3 inline mr-1" />Complete</Link>}
                {busy && busy !== 'approve' && <Loader2 className="w-4 h-4 animate-spin self-center" style={{ color: '#1E88E5' }} />}
              </div>
              {reassignOpen && (
                <div className="mt-2 rounded-lg p-3 space-y-2" style={{ background: '#1A2433', border: '1px solid #2D3748' }} data-testid="drill-reassign-panel">
                  <p className="text-xs" style={{ color: '#A0AEC0' }}>Tick the workers for this job (first ticked becomes the lead).</p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {staff.filter((u) => u.role !== 'admin' || crewIds.includes(u.id)).map((u) => (
                      <label key={u.id} className="flex items-center gap-2 text-xs text-white cursor-pointer">
                        <input type="checkbox" checked={crewIds.includes(u.id)} onChange={(e) => setCrewIds((c) => (e.target.checked ? [...c, u.id] : c.filter((x) => x !== u.id)))} className="accent-emerald-500" data-testid={`drill-crew-${u.id}`} />
                        {u.name} <span style={{ color: '#64748B' }}>· {u.role}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => saveReassign(data.job.id)} disabled={busy === 'reassign'} className="px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-60" style={{ background: '#00C896', color: '#0B1321' }} data-testid="drill-reassign-save">Save crew</button>
                    <button type="button" onClick={() => setReassignOpen(false)} className="px-3 py-1.5 rounded-lg text-xs" style={{ color: '#A0AEC0' }} data-testid="drill-reassign-cancel">Cancel</button>
                  </div>
                </div>
              )}
            </Section>
            <Section title="Crew">
              <div className="flex flex-wrap gap-2">
                {data.crew.length === 0 && <span className="text-xs" style={{ color: '#64748B' }}>Unassigned</span>}
                {data.crew.map((c) => <Link key={c.id} onClick={() => push('worker', c.id, c.name)}>{c.name}</Link>)}
              </div>
            </Section>
            <Section title="Hours by worker" testId="drill-job-workers"><Bars rows={data.hours_by_worker} nameKey="worker_name" onPick={(r) => r.worker_id && push('worker', r.worker_id, r.worker_name)} /></Section>
            {data.invoice && (
              <Section title="Invoice">
                <div className="flex items-center gap-2 text-sm"><Receipt className="w-4 h-4" style={{ color: '#10B981' }} />
                  <span className="text-white">{data.invoice.number || 'QBO'}</span><span style={{ color: '#A0AEC0' }}>· {data.invoice.status || '—'} · {fmtM(data.invoice.total)} · {fmtD(data.invoice.generated_at)}</span></div>
              </Section>
            )}
            <Related onNavigate={onNavigate} items={[
              { key: 'hours-by-location', label: 'Hours by Location (this site)', filters: { location: data.job.street_address || data.job.city || '', customer: data.job.customer_id || '', _from: `${data.job.title} (job site)` } },
              ...(data.job.customer_id ? [{ key: 'billing', label: 'Billing (this customer)', filters: { customer: data.job.customer_id, _from: `${data.job.customer_name} (customer)` } }] : []),
              { key: 'hours-by-job', label: 'Hours by Job', filters: { customer: data.job.customer_id || '', _from: `${data.job.title} (job)` } },
              { key: 'invoiced-jobs', label: 'Invoiced jobs', filters: { _from: `${data.job.title} (job)` } },
            ]} />
            <div className="flex flex-wrap gap-2 pt-1">
              <Link onClick={() => go(`/jobs?job=${data.job.id}`)} testId="drill-open-job"><ExternalLink className="w-3 h-3 inline mr-1" />Open job</Link>
            </div>
          </div>
        )}

        {data && cur.type === 'customer' && (
          <div className="mt-4 space-y-5">
            <p className="text-xs" style={{ color: '#A0AEC0' }}>{[data.customer.contact_name, data.customer.email, data.customer.phone, data.customer.address].filter(Boolean).join(' · ')}</p>
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Jobs (all)" value={data.summary.jobs_total} testId="drill-cust-jobs" />
              <Stat label="Completed in period" value={data.summary.completed_in_period} />
              <Stat label="Hours in period" value={fmtH(data.summary.hours)} />
              <Stat label="Billed" value={fmtM(data.summary.billed)} accent="#10B981" />
              <Stat label="Paid" value={fmtM(data.summary.paid)} />
              <Stat label="Outstanding" value={fmtM(data.summary.outstanding)} accent={data.summary.outstanding > 0 ? '#FFA000' : undefined} />
            </div>
            <Section title="Jobs by status">
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(data.status_counts).map(([k, v]) => (
                  <span key={k} className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: '#1A2433', color: '#CBD5E1', border: '1px solid #2D3748' }}>{k}: {v}</span>
                ))}
              </div>
            </Section>
            <Section title={`Open jobs (${data.open_jobs.length})`}><JobList jobs={data.open_jobs} onJob={(j) => push('job', j.id, j.title)} /></Section>
            <Section title="Hours by worker"><Bars rows={data.hours_by_worker} nameKey="worker_name" onPick={(r) => r.worker_id && push('worker', r.worker_id, r.worker_name)} /></Section>
            {data.invoices.length > 0 && (
              <Section title="Invoices in period">
                <div className="space-y-1 text-xs">
                  {data.invoices.map((i, idx) => (
                    <div key={idx} className="flex justify-between px-2 py-1 rounded" style={{ background: '#1A2433' }}>
                      <span className="text-white">{i.number} <span style={{ color: '#64748B' }}>{i.date}</span></span>
                      <span style={{ color: i.paid ? '#10B981' : '#A0AEC0' }}>{fmtM(i.total)} · {i.status}</span>
                    </div>
                  ))}
                </div>
              </Section>
            )}
            <Related onNavigate={onNavigate} items={[
              { key: 'hours-by-location', label: 'Hours by Location', filters: { customer: data.customer.id, location: '', _from: `${data.customer.name} (customer)` } },
              { key: 'customer-job-history', label: 'Customer job history', filters: { customer: data.customer.id, _from: `${data.customer.name} (customer)` } },
              { key: 'revenue-by-customer', label: 'Revenue by customer', filters: { _from: `${data.customer.name} (customer)` } },
              { key: 'billing', label: 'Billing', filters: { customer: data.customer.id, _from: `${data.customer.name} (customer)` } },
            ]} />
            <div className="flex flex-wrap gap-2 pt-1">
              <Link onClick={() => go(`/customers?customer=${data.customer.id}`)} testId="drill-open-customer"><ExternalLink className="w-3 h-3 inline mr-1" />Open customer</Link>
            </div>
          </div>
        )}

        {data && cur.type === 'project' && (
          <div className="mt-4 space-y-5">
            <p className="text-xs" style={{ color: '#A0AEC0' }}><span className="font-mono" style={{ color: '#10B981' }}>{data.project.project_number}</span> · {data.project.status}{data.project.customer_name ? ` · ${data.project.customer_name}` : ''}{data.project.is_system ? ' · catch-all' : ''}</p>
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Hours in period" value={fmtH(data.hours.total)} testId="drill-proj-hours" />
              <Stat label="Punches" value={data.hours.entries} />
              <Stat label="Workers" value={data.hours.workers} />
            </div>
            <Section title="Hours by worker"><Bars rows={data.hours_by_worker} nameKey="worker_name" onPick={(r) => push('worker', r.worker_id, r.worker_name)} /></Section>
            <Section title="Hours by job"><Bars rows={data.hours_by_job} nameKey="name" /></Section>
            <Section title={`Jobs (${data.jobs.length})`}><JobList jobs={data.jobs} onJob={(j) => push('job', j.id, j.title)} /></Section>
            <Related onNavigate={onNavigate} items={[
              { key: 'hours-by-project', label: 'Hours by Project', filters: { customer: data.project.customer_id || '', _from: `${data.project.name} (project)` } },
              { key: 'hours-by-location', label: 'Hours by Location', filters: { customer: data.project.customer_id || '', location: '', _from: `${data.project.name} (project)` } },
              { key: 'hours-by-job', label: 'Hours by Job', filters: { customer: data.project.customer_id || '', _from: `${data.project.name} (project)` } },
            ]} />
            <div className="flex flex-wrap gap-2 pt-1">
              {data.project.customer_id && <Link onClick={() => push('customer', data.project.customer_id, data.project.customer_name)}>Customer</Link>}
              <Link onClick={() => go('/settings?section=projects')}>Manage projects</Link>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// "Related reports" — pre-filtered jumps into other reports (the target shows a "Filtered from" chip).
const Related = ({ items, onNavigate }) => (
  <Section title="Related reports" testId="drill-related">
    <div className="flex flex-wrap gap-2">
      {items.map((it) => (
        <Link key={it.key + (it.label || '')} onClick={() => onNavigate?.(it.key, it.filters)} testId={`drill-related-${it.key}`}>{it.label}</Link>
      ))}
    </div>
  </Section>
);

const Link = ({ onClick, children, testId }) => (
  <button type="button" onClick={onClick} className="text-xs px-2.5 py-1.5 rounded-lg text-sky-300 hover:bg-sky-500/10" style={{ border: '1px solid #2D3748' }} data-testid={testId}>
    {children}
  </button>
);

